import React, { useState, useEffect, useCallback } from 'react';
import { Apple, ChevronLeft, ChevronRight } from 'lucide-react';
import { getPlayers, getNutritionDiary, saveNutritionGoals } from '../api';
import { useUrlParam } from '../hooks/useUrlParam';

// Own copy of the meal list (same convention as PlayerNutrition.js).
const MEALS = [
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'lunch',     label: 'Lunch' },
  { id: 'dinner',    label: 'Dinner' },
  { id: 'snack',     label: 'Snacks' },
];

const EMPTY = {
  entries: [],
  totals: { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, saturated_fat_g: 0, unsaturated_fat_g: 0 },
  goal: null,
  effectiveGoals: {
    kcal_goal: null, protein_g_goal: null, carbs_g_goal: null, fat_g_goal: null,
    protein_is_manual: false, carbs_is_manual: false,
  },
  autoGoals: null,
};

const tod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
function shiftDate(str, delta) {
  const d = new Date(str + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDay(str) {
  return new Date(str + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long' });
}
function fmtShort(d) {
  return d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—';
}

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px', marginBottom: 12 };
const inputStyle = { padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' };
const btn = (active, color = '#3b82f6') => ({
  padding: '7px 12px', borderRadius: 8, border: `1px solid ${active ? color : 'var(--border-strong)'}`,
  background: active ? color : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
  fontSize: 12, fontWeight: active ? 600 : 400, cursor: 'pointer', fontFamily: 'inherit',
});
const tag = (kind) => ({
  fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
  padding: '1px 5px', borderRadius: 4, marginLeft: 6,
  color: kind === 'auto' ? '#3b82f6' : 'var(--text-muted)',
  border: `1px solid ${kind === 'auto' ? 'rgba(59,130,246,0.4)' : 'var(--border-strong)'}`,
});

// Heuristic "on track" band — within 90–110 % of goal reads as on target; outside is flagged.
// Deliberately simple for v1; adjust the band here if the staff want it tighter/looser.
function statusFor(value, goal) {
  if (!goal) return { pct: null, label: '—', color: 'var(--text-dimmed)' };
  const pct = Math.round((value / goal) * 100);
  if (pct >= 90 && pct <= 110) return { pct, label: 'on track', color: '#10b981' };
  return { pct, label: pct < 90 ? 'under goal' : 'over goal', color: '#f59e0b' };
}

function TotalRow({ label, source, value, goal, unit }) {
  const s = statusFor(value, goal);
  const width = s.pct == null ? 0 : Math.min(100, s.pct);
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
        <span style={{ color: 'var(--text-secondary)' }}>
          {label}<span style={tag(source)}>{source}</span>
        </span>
        <span style={{ color: 'var(--text-muted)' }}>
          {value}{unit}{goal != null ? ` / ${goal}${unit}` : ''}
          {s.pct != null && <span style={{ color: s.color, fontWeight: 600 }}>{`  ·  ${s.pct}% (${s.label})`}</span>}
        </span>
      </div>
      {goal != null && (
        <div style={{ height: 6, background: 'var(--gauge-track)', borderRadius: 99, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${width}%`, background: s.color, borderRadius: 99 }} />
        </div>
      )}
    </div>
  );
}

// ── Read-only day breakdown (per meal, per food) ────────────────────────────
function DayBreakdown({ entries }) {
  if (!entries.length) {
    return <div style={{ fontSize: 13, color: 'var(--text-dimmed)', padding: '8px 0' }}>Nothing logged for this day.</div>;
  }
  return (
    <div>
      {MEALS.map(m => {
        const rows = entries.filter(e => e.meal_type === m.id);
        if (!rows.length) return null;
        const kcal = rows.reduce((s, e) => s + e.kcal, 0);
        return (
          <div key={m.id} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
              <span>{m.label}</span><span>{kcal} kcal</span>
            </div>
            {rows.map(e => (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '6px 10px', background: 'var(--bg-app)', borderRadius: 8, marginBottom: 4, fontSize: 12 }}>
                <span style={{ color: 'var(--text-primary)' }}>
                  {e.food_name}
                  <span style={{ color: 'var(--text-dimmed)' }}>
                    {'  '}{e.amount} {e.unit === 'portion' ? `portion (${e.grams} g)` : 'g'}
                  </span>
                </span>
                <span style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {e.kcal} kcal · P {e.protein_g} / C {e.carbs_g} / F {e.fat_g}
                </span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ── The auto protein/carb calculation, with its derivation — the suggestion the goals editor
// pre-fills and that applies whenever a macro is left on "Auto". ─────────────
function AutoGoalsPanel({ auto }) {
  const line = { display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 0' };
  return (
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
        Auto calculation <span style={tag('auto')}>suggestion</span>
      </div>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 10px' }}>
        From body weight and the match-day type. Used for any macro left on “Auto” above.
      </p>

      {!auto || auto.weight_kg == null ? (
        <div style={{ fontSize: 12, color: '#f59e0b' }}>
          No body weight on record for this player, so protein &amp; carb goals can’t be
          calculated. Add a weight in the player profile / weekly weigh-in.
        </div>
      ) : (
        <>
          <div style={line}>
            <span style={{ color: 'var(--text-secondary)' }}>Protein</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{auto.protein_g} g</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 6 }}>
            {auto.protein_g_per_kg} g/kg × {auto.weight_kg} kg — every day
          </div>
          <div style={line}>
            <span style={{ color: 'var(--text-secondary)' }}>Carbs</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{auto.carbs_g} g</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>
            {auto.carb_g_per_kg} g/kg × {auto.weight_kg} kg —{' '}
            {auto.carb_basis === 'md'
              ? `${auto.md_label} in the week schedule`
              : 'no match-day type for this date, using the default band'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
            Weight: {auto.weight_kg} kg
            {auto.weight_source ? ` (${auto.weight_source}${auto.weight_date ? `, ${fmtShort(auto.weight_date)}` : ''})` : ''}
          </div>
        </>
      )}
    </div>
  );
}

// ── Goals editor ──────────────────────────────────────────────────────────
// Calories & fat are always manual. Protein & carbs default to the auto calc (shown as the
// suggestion) but can be switched to Manual and overridden.

// Module-level so they aren't re-created every GoalsEditor render (which would drop input focus).
function NumField({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</label>
      <input type="number" min="0" step="1" value={value}
        onChange={e => onChange(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
    </div>
  );
}

function AutoManualRow({ label, suggestion, mode, value, onMode, onValue }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</span>
        <div style={{ display: 'flex', gap: 4 }}>
          <button style={{ ...btn(mode === 'auto'), padding: '3px 9px' }} onClick={() => onMode('auto')}>Auto</button>
          <button style={{ ...btn(mode === 'manual'), padding: '3px 9px' }} onClick={() => onMode('manual')}>Manual</button>
        </div>
      </div>
      {mode === 'auto' ? (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Using auto: <strong>{suggestion != null ? `${suggestion} g` : 'no weight on record'}</strong>
        </div>
      ) : (
        <input type="number" min="0" step="1" value={value}
          onChange={e => onValue(e.target.value)} style={{ ...inputStyle, width: '100%' }}
          placeholder={suggestion != null ? `auto suggestion: ${suggestion}` : ''} />
      )}
    </div>
  );
}

function GoalsEditor({ playerId, goal, auto, onSaved, user }) {
  const init = () => ({
    kcal_goal: goal?.kcal_goal ?? '',
    fat_g_goal: goal?.fat_g_goal ?? '',
    protein_mode: goal?.protein_is_manual ? 'manual' : 'auto',
    carbs_mode: goal?.carbs_is_manual ? 'manual' : 'auto',
    // prefill the manual input with the current override, else the auto suggestion
    protein_g_goal: goal?.protein_is_manual ? (goal.protein_g_goal ?? '') : (auto?.protein_g ?? ''),
    carbs_g_goal: goal?.carbs_is_manual ? (goal.carbs_g_goal ?? '') : (auto?.carbs_g ?? ''),
  });
  const [form, setForm] = useState(init);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { setForm(init()); setMsg(null); /* eslint-disable-next-line */ }, [playerId, goal, auto]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const numOk = v => v !== '' && Number(v) >= 0;

  const save = async () => {
    if (!numOk(form.kcal_goal)) return setMsg({ ok: false, text: 'Calories: enter a number of 0 or higher' });
    if (!numOk(form.fat_g_goal)) return setMsg({ ok: false, text: 'Fat: enter a number of 0 or higher' });
    if (form.protein_mode === 'manual' && !numOk(form.protein_g_goal)) return setMsg({ ok: false, text: 'Protein: enter a number of 0 or higher' });
    if (form.carbs_mode === 'manual' && !numOk(form.carbs_g_goal)) return setMsg({ ok: false, text: 'Carbs: enter a number of 0 or higher' });

    setSaving(true); setMsg(null);
    try {
      await saveNutritionGoals({
        player_id: playerId,
        updated_by: user?.id,
        kcal_goal: Number(form.kcal_goal),
        fat_g_goal: Number(form.fat_g_goal),
        protein_is_manual: form.protein_mode === 'manual',
        protein_g_goal: form.protein_mode === 'manual' ? Number(form.protein_g_goal) : null,
        carbs_is_manual: form.carbs_mode === 'manual',
        carbs_g_goal: form.carbs_mode === 'manual' ? Number(form.carbs_g_goal) : null,
      });
      setMsg({ ok: true, text: 'Goals saved.' });
      onSaved();
    } catch (e) {
      setMsg({ ok: false, text: e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Daily goals</div>
      <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '0 0 12px' }}>
        Calories &amp; fat are set by staff. Protein &amp; carbs default to the auto calculation
        (from body weight + match-day type) — switch to Manual to override.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <NumField label="Calories (kcal)" value={form.kcal_goal} onChange={v => set('kcal_goal', v)} />
        <NumField label="Fat (g)" value={form.fat_g_goal} onChange={v => set('fat_g_goal', v)} />
      </div>
      <AutoManualRow label="Protein (g)" suggestion={auto?.protein_g} mode={form.protein_mode}
        value={form.protein_g_goal} onMode={m => set('protein_mode', m)} onValue={v => set('protein_g_goal', v)} />
      <AutoManualRow label="Carbs (g)" suggestion={auto?.carbs_g} mode={form.carbs_mode}
        value={form.carbs_g_goal} onMode={m => set('carbs_mode', m)} onValue={v => set('carbs_g_goal', v)} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
        <button style={btn(true)} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save goals'}</button>
        {msg && <span style={{ fontSize: 12, fontWeight: 600, color: msg.ok ? '#10b981' : '#ef4444' }}>{msg.text}</span>}
      </div>
      {goal?.updated_at && (
        <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8 }}>
          Last updated: {new Date(goal.updated_at).toLocaleDateString('en-GB')}
        </div>
      )}
    </div>
  );
}

export default function Nutrition({ user }) {
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useUrlParam('player_id', '');
  const [date, setDate] = useUrlParam('date', tod());
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getPlayers()
      .then(p => {
        const active = (p || []).filter(pl => pl.active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setPlayers(active);
        if (!playerId && active.length) setPlayerId(String(active[0].id));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!playerId || !date) return;
    setLoading(true);
    try { setData({ ...EMPTY, ...(await getNutritionDiary(playerId, date)) }); }
    catch { setData(EMPTY); }
    finally { setLoading(false); }
  }, [playerId, date]);

  useEffect(() => { load(); }, [load]);

  const { totals, goal, effectiveGoals, autoGoals } = data;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Apple size={20} color="var(--text-primary)" />
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>Nutrition</h1>
      </div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 18px' }}>
        What a player logged each day in the Food Tracker. Calories and fat goals are set by
        staff; protein and carb goals are calculated from body weight and the match-day type,
        and can be overridden.
      </p>

      {/* Controls */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <select value={playerId} onChange={e => setPlayerId(e.target.value)} style={{ ...inputStyle, minWidth: 200 }}>
          <option value="">— select player —</option>
          {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(shiftDate(date, -1))}><ChevronLeft size={15} /></button>
          <input type="date" value={date} onChange={e => setDate(e.target.value || tod())} style={inputStyle} />
          <button style={{ ...btn(false), padding: 6 }} onClick={() => setDate(shiftDate(date, 1))}><ChevronRight size={15} /></button>
          <button style={btn(date === tod())} onClick={() => setDate(tod())}>Today</button>
        </div>
      </div>

      {!playerId ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>Select a player.</div>
      ) : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* Left — day view */}
          <div style={{ flex: '2 1 420px', minWidth: 0 }}>
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Day overview</div>
              <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 12 }}>{fmtDay(date)}</div>
              {loading ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div> : <DayBreakdown entries={data.entries} />}
            </div>

            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Day total vs. goal</div>
              <TotalRow label="Calories" source="manual" value={totals.kcal}      goal={effectiveGoals.kcal_goal}      unit=" kcal" />
              <TotalRow label="Protein"  source={effectiveGoals.protein_is_manual ? 'manual' : 'auto'} value={totals.protein_g} goal={effectiveGoals.protein_g_goal} unit=" g" />
              <TotalRow label="Carbs"    source={effectiveGoals.carbs_is_manual ? 'manual' : 'auto'}   value={totals.carbs_g}   goal={effectiveGoals.carbs_g_goal}   unit=" g" />
              <TotalRow label="Fat"      source="manual" value={totals.fat_g}     goal={effectiveGoals.fat_g_goal}     unit=" g" />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', padding: '0 0 3px 12px' }}>
                <span>— saturated</span><span>{totals.saturated_fat_g ?? 0} g</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', padding: '0 0 2px 12px' }}>
                <span>— unsaturated</span><span>{totals.unsaturated_fat_g ?? 0} g</span>
              </div>
              {!goal && <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 8 }}>No calorie / fat goal set for this player yet.</div>}
            </div>
          </div>

          {/* Right — goals */}
          <div style={{ flex: '1 1 300px', minWidth: 280 }}>
            <GoalsEditor playerId={playerId} goal={goal} auto={autoGoals} onSaved={load} user={user} />
            <AutoGoalsPanel auto={autoGoals} />
          </div>
        </div>
      )}
    </div>
  );
}
